import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Badge, List, ListSubheader, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { NavButton, colors } from '@truvhq/ui';
import { clsx } from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link as RouterLink, matchPath, useHistory, useLocation } from 'react-router-dom';

import { useFiltersContext } from 'contexts/FiltersContext';

import { useImpersonation } from 'hooks/impersonation';
import { useKeys } from 'hooks/keys';
import { useGetProfile } from 'hooks/profile';
import { useMembers } from 'hooks/team';
import { useAuth } from 'hooks/useAuth';

import { useGetBillingLink } from 'components/Billing/api';
import { getCustomSettingsTitle } from 'components/CustomSettings/utils';
import { SandboxSwitch } from 'components/SandboxSwitch/SandboxSwitch';
import { isNotApproved } from 'components/TeamMembers/tools';

import { ArrowExternal, Logo } from 'assets/icons';

import type { Permission, Permissions } from 'types/permissions';

import { urls } from 'urls';

type NavBarProps = {
  onMobileClose: () => void;
  openMobile: boolean;
};

type Item = {
  href?: string;
  info?: ReactNode;
  items?: Item[];
  submenu?: Item[];
  title: string;
  customAction?: () => void;
  permission?: Permission;
  companyPermission?: Permission;
  hide?: boolean;
};

type Section = {
  items: Item[];
  key: string;
  subheader: string;
};

function renderNavItems({
  companyPermissions,
  depth = 0,
  items,
  secondary,
  onOpened,
  pathname,
  permissions,
}: {
  companyPermissions: Permissions;
  depth?: number;
  items: Item[];
  secondary?: boolean;
  onOpened: (item: Item) => void;
  pathname: string;
  permissions: Permissions;
}) {
  return (
    <List disablePadding>
      {items.reduce<any>(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            companyPermissions,
            depth,
            item,
            secondary,
            onOpened,
            pathname,
            permissions,
          }),
        [],
      )}
    </List>
  );
}

export const isOpened = (pathname: string, href?: string) =>
  !!href &&
  !!matchPath(pathname, {
    path: href,
    exact: false,
  });

function reduceChildRoutes({
  acc,
  companyPermissions,
  depth,
  item,
  secondary,
  onOpened,
  pathname,
  permissions,
}: {
  acc: any[];
  companyPermissions: Permissions;
  depth: number;
  item: Item;
  secondary?: boolean;
  onOpened: (item: Item) => void;
  pathname: string;
  permissions: Permissions;
}) {
  if (
    !item ||
    item.hide ||
    (item.permission && !permissions.has(item.permission)) ||
    (item.companyPermission && !companyPermissions.has(item.companyPermission))
  ) {
    return acc;
  }

  const key = item.title + depth;
  const opened = isOpened(pathname, item.href);

  if (opened) {
    onOpened(item);
  }

  acc.push(
    <div key={key} className={clsx(item.info && 'group relative')}>
      <NavButton
        active={opened}
        component={RouterLink}
        secondary={secondary}
        title={item.title}
        to={{ pathname: item.href ?? '', state: { internal: true } }}
        onClick={item.customAction}
      >
        {item.items &&
          renderNavItems({
            companyPermissions,
            depth: depth + 1,
            items: item.items,
            secondary,
            onOpened,
            pathname,
            permissions,
          })}
      </NavButton>
      {item.info}
    </div>,
  );

  return acc;
}

const StyledBadge = withStyles(() => ({
  root: {
    position: 'absolute',
    top: '50%',
    right: 0,
  },
  badge: {
    top: 0,
    right: 24,
    background: colors.grey30,
    color: colors.grey100,
  },
}))(Badge);

const InfoExternal: FC<{ className: string }> = ({ className }) => (
  <ArrowExternal
    className={clsx(
      'pointer-events-none absolute right-6 top-1/2 mt-[1px] -translate-y-1/2 transform opacity-0 transition-opacity group-hover:opacity-100',
      className,
    )}
  />
);

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const location = useLocation();
  const { logout, permissions, companyPermissions } = useAuth();
  const { data: profileSettings } = useGetProfile();

  const history = useHistory();
  const { resetFilters } = useFiltersContext();
  const { keys } = useKeys();
  const { stopImpersonation } = useImpersonation();

  const { data, refetch } = useGetBillingLink(window.location.origin + urls.settings.url());

  const [billingTab, setBillingTab] = useState<Window | null>(null);

  const handleBillingClick = () => {
    setBillingTab(window.open());

    return refetch();
  };

  useEffect(() => {
    if (data?.url && billingTab) {
      billingTab.location.href = data.url;
      setBillingTab(null);
    }
  }, [billingTab, data]);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const [currentSubmenuItem, setCurrentSubmenuItem] = useState<Item | null>();

  const onOpened = useCallback(
    (item: Item) => {
      if (currentSubmenuItem?.href !== item.href && item.submenu) {
        setCurrentSubmenuItem(item);
      }
    },
    [currentSubmenuItem],
  );

  const sandboxWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentSubmenuItem && sandboxWrapperRef.current) {
      const scrollTargetElement = document.querySelector(`[data-testid=${currentSubmenuItem.title}]`);

      if (
        scrollTargetElement &&
        scrollTargetElement.getBoundingClientRect().bottom > sandboxWrapperRef.current.getBoundingClientRect().top
      ) {
        scrollTargetElement?.scrollIntoView();
      }
    }
  }, [currentSubmenuItem]);

  const handleLogout = async (): Promise<void> => {
    try {
      resetFilters();
      stopImpersonation();
      await logout();
      history.push('/');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const canViewEmbedded = permissions.has('can_view_embedded');

  const enableMembersRequest = permissions.has('can_invite_users') && permissions.has('can_view_company_settings');

  const { data: members } = useMembers(enableMembersRequest);

  const notApprovedMembers = members?.filter(isNotApproved).length;

  const role = profileSettings?.role.id;
  const isDeveloper = role === 'developer';
  const isOrdersManager = role === 'orders_manager';
  const isStaff = !!profileSettings?.staff_dashboard_url;

  const newKeys = isDeveloper && keys?.hasNewProdOrDevKeys ? 1 : 0;

  const sections: Section[] = [
    {
      subheader: '',
      key: 'main',
      items: [
        {
          title: urls.home.title,
          href: urls.home.url(),
          permission: urls.home.permission,
          hide: isOrdersManager,
        },
        {
          title: urls.emulator.title,
          href: urls.emulator.url(),
          permission: urls.emulator.permission,
        },
        {
          title: urls.usage.title,
          items: [
            {
              title: urls.users.title,
              href: urls.users.url(),
              permission: urls.users.permission,
            },
            {
              title: urls.orders.title,
              href: urls.orders.url(),
              permission: urls.orders.permission,
            },
            {
              title: urls.tasks.title,
              href: urls.tasks.url(),
              permission: urls.tasks.permission,
            },
            {
              title: urls.coverage.title,
              href: urls.coverage.url(),
              permission: urls.coverage.permission,
              hide: isDeveloper || isOrdersManager,
            },
          ],
        },
        {
          title: urls.development.title,
          permission: urls.development.permission,
          items: [
            {
              title: urls.workflows.title,
              href: urls.workflows.url(),
              permission: urls.workflows.permission,
              hide: !canViewEmbedded,
            },
            {
              title: urls.keys.title,
              href: urls.keys.url(),
              permission: urls.keys.permission,
              info: <StyledBadge badgeContent={newKeys} color="secondary" />,
            },
            {
              title: urls.platforms.title,
              href: urls.platforms.url(),
              permission: urls.platforms.permission,
            },
            {
              title: urls.logs.title,
              href: urls.logs.url(),
              permission: urls.logs.permission,
            },
            {
              title: urls.webhooks.title,
              href: urls.webhooks.url(),
              permission: urls.webhooks.permission,
            },
            // {
            //   title: urls.systemStatus.title,
            //   href: urls.systemStatus.url(),
            //   permission: urls.systemStatus.permission,
            // },
          ],
        },
        {
          title: urls.customization.title,
          permission: urls.customization.permission,
          items: [
            {
              title: urls.marketplace.title,
              href: urls.marketplace.url(),
              permission: urls.marketplace.permission,
              hide: !isStaff,
            },
            {
              title: urls.account.title,
              href: urls.account.url(),
              permission: urls.account.permission,
              submenu: [
                {
                  title: urls.branding.title,
                  href: urls.branding.url(),
                  permission: urls.branding.permission,
                },
                {
                  title: getCustomSettingsTitle(permissions),
                  href: urls.customSettings.url(),
                  permission: urls.customSettings.permission,
                },
              ],
            },
            {
              title: urls.templates.title,
              href: urls.templates.url(),
              permission: urls.templates.permission,
            },
            {
              title: urls.fieldsCustomization.title,
              href: urls.fieldsCustomization.url(),
              permission: urls.fieldsCustomization.permission,
            },
          ],
        },
        {
          title: 'Help',
          permission: urls.supportTickets.permission,
          items: [
            {
              title: urls.supportTickets.title,
              href: urls.supportTickets.url(),
              permission: urls.supportTickets.permission,
            },
            {
              title: urls.docs.title,
              customAction() {
                window.open(urls.docs.parameterUrl?.(isOrdersManager), '_blank');
              },
              permission: urls.docs.permission,
              info: <InfoExternal className="text-white" />,
            },
            {
              title: urls.feedback.title,
              customAction() {
                window.open(urls.feedback.url(), '_blank');
              },
              info: <InfoExternal className="text-white" />,
            },
          ],
        },
      ],
    },
    {
      subheader: '',
      key: 'bottom',
      items: [
        {
          title: urls.settings.title,
          href: urls.settings.url(),
          submenu: [
            {
              title: urls.company.title,
              href: urls.company.url(),
              permission: urls.company.permission,
            },
            {
              title: urls.team.title,
              href: urls.team.parameterUrl?.() ?? '/',
              info: <StyledBadge badgeContent={notApprovedMembers} color="secondary" />,
              permission: urls.team.permission,
            },
            {
              title: urls.billing.title,
              permission: urls.billing.permission,
              customAction: handleBillingClick,
              info: <InfoExternal className="text-grey100" />,
            },
            {
              title: urls.invoices.title,
              href: urls.invoices.url(),
              permission: urls.billing.permission,
            },
            {
              title: 'Profile',
              href: urls.profile.url(),
            },
            {
              title: 'Log out',
              customAction: handleLogout,
            },
          ],
        },
      ],
    },
  ];

  return (
    <>
      <PerfectScrollbar className="flex max-h-screen w-[216px] flex-grow flex-col bg-grey90">
        <div className="flex justify-start py-8 pl-6">
          <RouterLink to="/">
            <Logo className="h-5 w-auto text-white" />
          </RouterLink>
        </div>
        <div className="relative flex flex-grow flex-col bg-grey90 pt-3">
          {sections.map((section, index) => (
            <List
              key={section.key}
              className={clsx({
                'mt-[-12px]': index < 1 && section.items[0].hide,
                'mt-2 border-t-[1px] border-t-grey40/10 pt-2': index > 0,
              })}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                companyPermissions,
                items: section.items,
                onOpened,
                pathname: location.pathname,
                permissions,
              })}
            </List>
          ))}
        </div>
        <div ref={sandboxWrapperRef} className="sticky bottom-0 flex flex-col bg-white/10 py-1.5 backdrop-blur-lg">
          {isStaff && (
            <NavButton
              title="Staff dashboard"
              onClick={() => {
                window.open(profileSettings.staff_dashboard_url, '_blank');
              }}
            />
          )}
          <div className="pl-4">
            <SandboxSwitch invert />
          </div>
        </div>
      </PerfectScrollbar>
      {currentSubmenuItem?.submenu?.length && (
        <div className="flex max-h-screen w-[216px] flex-grow flex-col border-r border-r-grey30 bg-white">
          <Typography className="mx-4 mb-3 mt-5 px-2 py-1.5" variant="h3">
            {currentSubmenuItem.title}
          </Typography>
          <List>
            {renderNavItems({
              companyPermissions,
              items: currentSubmenuItem.submenu,
              secondary: true,
              onOpened,
              pathname: location.pathname,
              permissions,
            })}
          </List>
        </div>
      )}
    </>
  );
};

export default NavBar;
